import { averageScore, isScored } from '@/lib/scoreStatistics';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Download, FileText, Table } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface DashboardExportProps {
  dateRange: string;
}

export function DashboardExport({ dateRange }: DashboardExportProps) {
  const [isExporting, setIsExporting] = useState(false);

  const getDaysFromRange = () => {
    if (dateRange === '7days') return 7;
    if (dateRange === '90days') return 90;
    return 30;
  };

  const getDateFilter = () => {
    const days = getDaysFromRange();
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString();
  };

  // Fetch all data needed for export
  const fetchExportData = async () => {
    const dateFilter = getDateFilter();

    // Fetch evaluations
    const { data: evaluations } = await supabase
      .from('simulation_evaluations')
      .select('*')
      .gte('created_at', dateFilter)
      .eq('is_submitted', true)
      .is('deleted_at', null);

    // Fetch users
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, display_name, email, company_name, created_at');

    // Fetch agents
    const { data: agents } = await supabase
      .from('simulation_agents')
      .select('id, name, type, specialty, avg_score, completions, status');

    // Fetch companies
    const { data: companies } = await supabase
      .from('companies')
      .select('id, name, is_active');

    return { evaluations, profiles, agents, companies };
  };

  const exportToCSV = async (type: 'summary' | 'evaluations' | 'users' | 'agents') => {
    setIsExporting(true);
    try {
      const data = await fetchExportData();
      let csvContent = '';
      let filename = '';
      const days = getDaysFromRange();

      switch (type) {
        case 'summary':
          filename = `dashboard-summary-${days}days-${new Date().toISOString().split('T')[0]}.csv`;
          csvContent = generateSummaryCSV(data);
          break;
        case 'evaluations':
          filename = `evaluations-${days}days-${new Date().toISOString().split('T')[0]}.csv`;
          csvContent = generateEvaluationsCSV(data.evaluations || []);
          break;
        case 'users':
          filename = `users-${new Date().toISOString().split('T')[0]}.csv`;
          csvContent = generateUsersCSV(data.profiles || []);
          break;
        case 'agents':
          filename = `agents-${new Date().toISOString().split('T')[0]}.csv`;
          csvContent = generateAgentsCSV(data.agents || []);
          break;
      }

      downloadCSV(csvContent, filename);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} exported successfully`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  const generateSummaryCSV = (data: any) => {
    const { evaluations = [], profiles = [], agents = [], companies = [] } = data;
    const days = getDaysFromRange();
    
    const totalEvaluations = evaluations.length;
    const avgScore = averageScore(evaluations);
    const uniqueUsers = new Set(evaluations.map((e: any) => e.user_id)).size;
    const activeAgents = agents.filter((a: any) => a.status === 'active').length;

    const lines = [
      'MEDD-SIM Platform Dashboard Summary',
      `Report Period,Last ${days} days`,
      `Generated,${new Date().toLocaleString()}`,
      '',
      'KEY METRICS',
      'Metric,Value',
      `Total Users,${profiles.length}`,
      `Active Companies,${companies.filter((c: any) => c.is_active).length}`,
      `Total Agents,${agents.length}`,
      `Active Agents,${activeAgents}`,
      '',
      `PERFORMANCE (Last ${days} days)`,
      'Metric,Value',
      `Total Sessions,${totalEvaluations}`,
      `Average Score,${avgScore === null ? 'Not scored' : `${avgScore}%`}`,
      `Unique Active Users,${uniqueUsers}`,
      '',
      'TOP PERFORMING AGENTS',
      'Agent Name,Type,Avg Score,Completions',
      ...agents
        .filter((a: any) => isScored(a.avg_score))
        .sort((a: any, b: any) => (b.avg_score || 0) - (a.avg_score || 0))
        .slice(0, 10)
        .map((a: any) => `"${a.name}",${a.type},${Math.round(a.avg_score || 0)}%,${a.completions || 0}`),
    ];

    return lines.join('\n');
  };

  const generateEvaluationsCSV = (evaluations: any[]) => {
    const headers = ['Date', 'User', 'Email', 'Agent', 'Score', 'Company', 'Platform'];
    const rows = evaluations.map((e) => [
      new Date(e.created_at).toLocaleDateString(),
      `"${e.user_name || 'Unknown'}"`,
      e.user_email || '',
      `"${e.agent_name || 'Unknown'}"`,
      isScored(e.overall_score) ? e.overall_score : 'Not scored',
      `"${e.company_name || ''}"`,
      e.agent_type || '',
    ]);

    return [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  };

  const generateUsersCSV = (profiles: any[]) => {
    const headers = ['Name', 'Email', 'Company', 'Created Date'];
    const rows = profiles.map((p) => [
      `"${p.display_name || ''}"`,
      p.email || '',
      `"${p.company_name || ''}"`,
      new Date(p.created_at).toLocaleDateString(),
    ]);

    return [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  };

  const generateAgentsCSV = (agents: any[]) => {
    const headers = ['Name', 'Type', 'Specialty', 'Avg Score', 'Completions', 'Status'];
    const rows = agents.map((a) => [
      `"${a.name || ''}"`,
      a.type || '',
      `"${a.specialty || ''}"`,
      a.avg_score ? `${Math.round(a.avg_score)}%` : 'N/A',
      a.completions || 0,
      a.status || '',
    ]);

    return [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  };

  const downloadCSV = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2" disabled={isExporting}>
          <Download className="h-4 w-4" />
          {isExporting ? 'Exporting...' : 'Export'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>Export Reports</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => exportToCSV('summary')} className="gap-2">
          <FileText className="h-4 w-4" />
          Dashboard Summary
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => exportToCSV('evaluations')} className="gap-2">
          <Table className="h-4 w-4" />
          All Evaluations
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => exportToCSV('users')} className="gap-2">
          <Table className="h-4 w-4" />
          All Users
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => exportToCSV('agents')} className="gap-2">
          <Table className="h-4 w-4" />
          All Agents
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
